import { useCallback, useState } from 'react';
import { erc20Abi } from 'viem';
import {
  PERMIT2_ADDRESS,
  AllowanceTransfer,
  AllowanceProvider,
  type PermitSingleData,
  type PermitBatchData,
} from '@uniswap/permit2-sdk';
import { useAccount, useChainId } from 'wagmi';
import { useWriteContractWithReceipt } from '@/onchain/hooks/useWriteContractWithReceipt';
import { useEthersProvider, useEthersSigner } from '@/onchain/utils/ethersUtils';
import { Permit2ApproveParams } from '@/types';

export type PermitDetails = {
  amount: bigint;
  token: `0x${string}`;
};

export type Permit = {
  // support both single and batch permit
  details: PermitDetails[];
  spender: `0x${string}`;
  expiration: number;
};

export const usePermit2Signature = () => {
  const [permitData, setPermitData] = useState<PermitSingleData | PermitBatchData | undefined>();
  const [signature, setSignature] = useState<string | undefined>();
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  // get current address
  const account = useAccount();
  const chainId = useChainId();

  const provider = useEthersProvider({ chainId });
  const signer = useEthersSigner({ chainId });

  const reset = useCallback(() => {
    setPermitData(undefined);
    setSignature(undefined);
    setIsConfirmLoading(false);
    setError(undefined);
  }, []);

  const signPermit = useCallback(
    async ({
      permit,
      forceBatchPermit = false,
    }: {
      permit: Permit;
      forceBatchPermit?: boolean;
    }) => {
      if (!provider || !signer || !account || !chainId) return;
      setIsConfirmLoading(true);
      try {
        const allowanceProvider = new AllowanceProvider(provider, PERMIT2_ADDRESS);

        // get a list of nonces for each approval
        const nonceList = await Promise.all(
          permit.details.map(async (detail) =>
            allowanceProvider.getNonce(detail.token, account.address as string, permit.spender),
          ),
        );
        const { expiration } = permit;
        const permitDataTmp = AllowanceTransfer.getPermitData(
          {
            details:
              permit.details.length == 1 && !forceBatchPermit
                ? {
                    ...permit.details[0],
                    // set nonce to the current nonce
                    nonce: nonceList[0],
                    expiration,
                  }
                : permit.details.map((detail, index) => ({
                    ...detail,
                    // set nonce to the current nounce
                    nonce: nonceList[index],
                    expiration,
                  })),
            spender: permit.spender,
            sigDeadline: expiration,
          },
          PERMIT2_ADDRESS,
          chainId,
        );
        // eslint-disable-next-line no-underscore-dangle
        const signedSignature = await signer._signTypedData(
          permitDataTmp.domain,
          permitDataTmp.types,
          permitDataTmp.values,
        );

        setPermitData(permitDataTmp);
        setSignature(signedSignature);
      } catch (e) {
        const formattedError = (e as Error)
          ? {
              ...(e as Error),
              message:
                typeof (e as Error).message === 'object'
                  ? 'Failed to sign permit2 message'
                  : (e as Error).message,
            }
          : null;
        setError(formattedError as Error);
      } finally {
        setIsConfirmLoading(false);
      }
    },
    [account, chainId, provider, signer],
  );

  return {
    signPermit,
    permitData,
    signature,
    isConfirmLoading,
    error,
    reset,
  };
};

export const usePermit2ApproveAllowance = (permit2ApproveParams: Permit2ApproveParams) => {
  const { spender, amount, tokenAddress } = permit2ApproveParams;

  const {
    hash,
    // write state
    writeError,
    isWriteError,
    isWritePending,
    // confirm state
    confirmError,
    isConfirmLoading,
    isConfirmSuccess,
    isConfirmError,
    // tx receipt
    receipt,
    // write function
    writeContract,
    writeContractAsync,
    reset,
  } = useWriteContractWithReceipt();

  const write = useCallback(() => {
    writeContract({
      address: tokenAddress,
      abi: erc20Abi,
      functionName: 'approve',
      args: [spender, amount],
    });
  }, [spender, amount, tokenAddress, writeContract]);

  const writeAsync = useCallback(async () => {
    await writeContractAsync({
      address: tokenAddress,
      abi: erc20Abi,
      functionName: 'approve',
      args: [spender, amount],
    });
  }, [spender, amount, tokenAddress, writeContractAsync]);

  return {
    hash,
    writeError,
    isWriteError,
    isWritePending,
    confirmError,
    isConfirmLoading,
    isConfirmSuccess,
    isConfirmError,
    receipt,
    write,
    writeAsync,
    reset,
  };
};
